import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getPatient, getPatientConsultations } from "../lib/api";
import {
  collectClinicalSummary,
  collectRegisteredMedications,
  collectStoredAlerts,
  consultationDateRaw,
  findLatestVgiConsultation,
  formatDisplayDate,
  friendlyRecordNumber,
  listScaleEntries,
  listScalesByConsultation,
  listVitalRows,
  patientFullName,
  patientInitials,
  sexLabel,
  sortConsultationsNewestFirst,
  summarizeText,
} from "../lib/patientDashboardV1";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DashboardSectionEmpty from "../components/patient-dashboard-v1/DashboardSectionEmpty";
import ExternalAssessmentsSection from "../components/ExternalAssessmentsSection";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Calendar,
  ClipboardPlus,
  FileText,
  Loader2,
  Pencil,
  Pill,
  Stethoscope,
  User,
} from "lucide-react";

function MetaItem({ label, value }) {
  if (!value) return null;
  return (
    <div className="min-w-0">
      <p className="text-sm text-slate-600">{label}</p>
      <p className="text-base font-medium text-slate-900 break-words">{value}</p>
    </div>
  );
}

export default function PatientDashboardV1() {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const [patientRes, consultRes] = await Promise.all([
          getPatient(id),
          getPatientConsultations(id),
        ]);
        if (cancelled) return;
        setPatient(patientRes.data);
        setConsultations(sortConsultationsNewestFirst(consultRes.data || []));
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
          setPatient(null);
          setConsultations([]);
        } else {
          setError("No se pudo cargar el expediente. Intente de nuevo.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const latestConsultation = consultations[0] || null;
  const latestVgi = useMemo(
    () => findLatestVgiConsultation(consultations),
    [consultations]
  );
  const alerts = collectStoredAlerts(latestConsultation);
  const medications = collectRegisteredMedications(patient, latestConsultation);
  const vitals = listVitalRows(latestConsultation?.vitals);
  const vitalDate = vitals.length
    ? formatDisplayDate(consultationDateRaw(latestConsultation))
    : null;
  const scaleGroups = useMemo(
    () => listScalesByConsultation(consultations),
    [consultations]
  );
  const latestVgiScales = listScaleEntries(latestVgi?.escalas);
  const lastConsultDate = formatDisplayDate(consultationDateRaw(latestConsultation));
  const name = patientFullName(patient);
  const sex = sexLabel(patient);
  const recordNumber = friendlyRecordNumber(patient);
  const clinicalSummary = collectClinicalSummary(patient, latestConsultation);
  const ageLabel =
    patient?.edad != null && patient.edad !== "" ? `${patient.edad} años` : "Sin edad registrada";

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 flex flex-col items-center gap-3 text-slate-700" data-testid="patient-dashboard-v1-loading">
        <Loader2 className="h-8 w-8 animate-spin text-sky-700" />
        <p className="text-sm">Cargando expediente…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="patient-dashboard-v1-not-found">
        <DashboardSectionEmpty
          icon={User}
          title="Paciente no encontrado"
          description="No hay un expediente con este identificador, o no pertenece a su cuenta."
        />
        <div className="mt-4 text-center">
          <Button asChild variant="outline">
            <Link to="/pacientes">Volver a pacientes</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="patient-dashboard-v1-error">
        <DashboardSectionEmpty icon={AlertTriangle} title="Error al cargar" description={error} />
        <div className="mt-4 text-center">
          <Button asChild variant="outline">
            <Link to="/pacientes">Volver a pacientes</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isArchived = String(patient?.status || "").toLowerCase() === "archived";

  return (
    <div className="max-w-6xl mx-auto px-4 py-5 space-y-4 overflow-x-hidden" data-testid="patient-dashboard-v1">
      <p className="text-sm text-slate-600">
        Vista de prueba del expediente. El perfil actual no se modifica.
      </p>

      <Card className="border-slate-200">
        <CardContent className="pt-5 pb-5">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <Avatar className="h-14 w-14 shrink-0 bg-sky-100 text-sky-800">
              <AvatarFallback className="bg-sky-100 text-sky-800 text-lg font-semibold">
                {patientInitials(patient)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-semibold text-slate-900 leading-tight break-words">
                {name || "Paciente sin nombre"}
              </h1>
              {isArchived && (
                <Badge variant="outline" className="mt-2 border-slate-300 text-slate-700 bg-slate-50">
                  Archivado
                </Badge>
              )}
              {recordNumber && (
                <p className="text-sm text-slate-700 mt-1">Expediente: {recordNumber}</p>
              )}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                <MetaItem label="Edad" value={ageLabel} />
                <MetaItem label="Sexo" value={sex} />
                <MetaItem label="Última consulta" value={lastConsultDate || "Sin consultas"} />
                <MetaItem label="Teléfono" value={patient?.telefono} />
                <MetaItem label="Cuidador" value={patient?.cuidador_principal} />
                <MetaItem label="Teléfono del cuidador" value={patient?.telefono_cuidador} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isArchived && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-testid="archived-patient-banner">
          Paciente archivado. El expediente y sus consultas se conservan. Restaure al paciente desde la lista de archivados para iniciar o continuar atención.
        </div>
      )}

      <ExternalAssessmentsSection patientId={id} archived={isArchived} />

      <div className="flex flex-wrap gap-2">
        <Button className="bg-sky-700 hover:bg-sky-800" disabled={isArchived} asChild={!isArchived}>
          {isArchived ? (
            <>
              <ClipboardPlus className="h-4 w-4 mr-2" />
              Nueva consulta
            </>
          ) : (
            <Link to={`/nueva-consulta/${id}`}>
              <ClipboardPlus className="h-4 w-4 mr-2" />
              Nueva consulta
            </Link>
          )}
        </Button>
        <Button asChild variant="outline">
          <Link to={`/pacientes/${id}/editar`}>
            <Pencil className="h-4 w-4 mr-2" />
            Editar paciente
          </Link>
        </Button>
        <Button asChild variant="ghost">
          <Link to={`/pacientes/${id}`}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al perfil clásico
          </Link>
        </Button>
      </div>
      <p className="text-sm text-slate-600">
        El expediente conserva consultas, medicamentos, documentos y evaluaciones ya registradas.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <SummaryStat
          label="Última consulta"
          value={lastConsultDate || "Sin consultas"}
          muted={!latestConsultation}
        />
        <SummaryStat
          label="Evaluaciones registradas"
          value={
            latestVgi
              ? `${formatDisplayDate(consultationDateRaw(latestVgi)) || "Fecha no registrada"} · ${latestVgiScales.length} ${latestVgiScales.length === 1 ? "escala" : "escalas"}`
              : "Sin escalas"
          }
          muted={!latestVgi}
        />
        <SummaryStat
          label="Alertas"
          value={alerts.length ? `${alerts.length} registradas` : "No hay alertas registradas en esta consulta."}
          muted={!alerts.length}
        />
        <SummaryStat
          label="Medicamentos registrados"
          value={medications.items.length ? String(medications.items.length) : "Sin medicamentos"}
          muted={!medications.items.length}
        />
      </div>

      <Tabs defaultValue="resumen" className="space-y-3">
        <TabsList className="flex flex-wrap h-auto w-full justify-start gap-1">
          <TabsTrigger value="resumen">Resumen</TabsTrigger>
          <TabsTrigger value="consultas">Consultas</TabsTrigger>
          <TabsTrigger value="vgi">Evaluaciones registradas</TabsTrigger>
          <TabsTrigger value="medicamentos">Medicamentos</TabsTrigger>
          <TabsTrigger value="documentos">Documentos</TabsTrigger>
          <TabsTrigger value="citas">Citas</TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-slate-900">Resumen clínico</CardTitle>
              </CardHeader>
              <CardContent>
                {clinicalSummary.hasAny || alerts.length ? (
                  <div className="space-y-3">
                    {alerts.length > 0 && (
                      <SummaryBlock title="Alertas registradas">
                        <ul className="space-y-1">
                          {alerts.map((a, i) => (
                            <li key={i} className="text-sm text-slate-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                              {a}
                            </li>
                          ))}
                        </ul>
                      </SummaryBlock>
                    )}
                    <SummaryBlock title="Alergias registradas" items={clinicalSummary.alergias} />
                    <SummaryBlock title="Diagnósticos de la última consulta" items={clinicalSummary.diagnosticos} />
                    <SummaryBlock title="Antecedentes relevantes" items={clinicalSummary.antecedentes} />
                    <SummaryBlock title="Síndromes geriátricos registrados" items={clinicalSummary.sindromes} />
                  </div>
                ) : (
                  <p className="text-sm text-slate-700">No hay información clínica resumida disponible.</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-slate-900">
                  <Stethoscope className="h-4 w-4 text-sky-700" />
                  Últimos signos vitales
                </CardTitle>
              </CardHeader>
              <CardContent>
                {vitals.length ? (
                  <div>
                    {vitalDate && (
                      <p className="text-sm text-slate-600 mb-3">Medición: {vitalDate}</p>
                    )}
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                      {vitals.map((v) => (
                        <div key={v.key} className="min-w-0">
                          <dt className="text-sm text-slate-600">{v.label}</dt>
                          <dd className="text-base font-medium text-slate-900">{v.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ) : (
                  <DashboardSectionEmpty
                    compact
                    icon={Stethoscope}
                    title="Sin signos vitales"
                    description="No hay vitales en la última consulta."
                  />
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-slate-900">
                  <Pill className="h-4 w-4 text-sky-700" />
                  Medicamentos registrados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MedicationList medications={medications} />
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-slate-900">
                  <FileText className="h-4 w-4 text-sky-700" />
                  Actividad reciente
                </CardTitle>
              </CardHeader>
              <CardContent>
                {consultations.length ? (
                  <ul className="space-y-3">
                    {consultations.slice(0, 5).map((c) => (
                      <li key={c.id} className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">
                          {formatDisplayDate(consultationDateRaw(c)) || "Fecha no registrada"}
                        </p>
                        <p className="text-sm text-slate-700">
                          {c.tipo_consulta || "Consulta"}
                          {c.motivo_consulta ? ` · ${summarizeText(c.motivo_consulta)}` : ""}
                        </p>
                        <Link to={`/consulta/${c.id}`} className="text-sm text-sky-800 hover:underline">
                          Abrir consulta
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <DashboardSectionEmpty
                    compact
                    icon={FileText}
                    title="Sin consultas"
                    description="Aún no hay actividad clínica registrada."
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="consultas">
          <Card className="border-slate-200">
            <CardContent className="pt-5">
              {consultations.length ? (
                <ul className="divide-y divide-slate-100">
                  {consultations.map((c) => (
                    <li key={c.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900">
                          {formatDisplayDate(consultationDateRaw(c)) || "Fecha no registrada"}
                        </p>
                        <p className="text-sm text-slate-700">
                          {c.tipo_consulta || "Consulta"}
                          {c.motivo_consulta ? ` · ${summarizeText(c.motivo_consulta)}` : ""}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 shrink-0">
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/consulta/${c.id}`}>Ver</Link>
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <DashboardSectionEmpty
                  icon={ClipboardPlus}
                  title="Sin consultas"
                  description="Este paciente todavía no tiene consultas en el expediente."
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vgi">
          <Card className="border-slate-200">
            <CardContent className="pt-5">
              {scaleGroups.length ? (
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-medium text-slate-900 mb-2">Resumen inicial</p>
                    <p className="text-sm text-slate-700 mb-3">
                      {scaleGroups[0].dateLabel || "Fecha no registrada"} · valores e interpretaciones guardados, sin recalcular.
                    </p>
                    <ScaleGrid scales={scaleGroups[0].scales.slice(0, 4)} />
                    {scaleGroups[0].consultationId && (
                      <Button asChild variant="outline" size="sm" className="mt-3">
                        <Link to={`/consulta/${scaleGroups[0].consultationId}`}>Abrir esa consulta</Link>
                      </Button>
                    )}
                  </div>
                  {scaleGroups.map((group) => (
                    <div key={group.consultationId || group.dateRaw} className="border-t border-slate-100 pt-4">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <p className="text-base font-medium text-slate-900">
                          {group.dateLabel || "Fecha no registrada"}
                        </p>
                        {group.consultationId && (
                          <Link to={`/consulta/${group.consultationId}`} className="text-sm text-sky-800 hover:underline">
                            Abrir consulta
                          </Link>
                        )}
                      </div>
                      <ScaleGrid scales={group.scales} />
                    </div>
                  ))}
                </div>
              ) : (
                <DashboardSectionEmpty
                  icon={Activity}
                  title="Sin escalas"
                  description="No hay escalas geriátricas guardadas en las consultas de este paciente."
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="medicamentos">
          <Card className="border-slate-200">
            <CardContent className="pt-5">
              <MedicationList medications={medications} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documentos">
          <Card className="border-slate-200">
            <CardContent className="pt-5">
              {consultations.length ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-700">
                    Los documentos se generan desde cada consulta. Esta vista no crea archivos nuevos.
                  </p>
                  <ul className="divide-y divide-slate-100">
                    {consultations.map((c) => (
                      <li key={c.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <span className="text-sm text-slate-800">
                          {formatDisplayDate(consultationDateRaw(c)) || "Consulta"}
                        </span>
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/entregable/${c.id}`}>Generar documentos</Link>
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <DashboardSectionEmpty
                  icon={FileText}
                  title="Sin documentos"
                  description="No hay consultas desde las que generar recetas, planes u otros informes."
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="citas">
          <Card className="border-slate-200">
            <CardContent className="pt-5">
              <DashboardSectionEmpty
                icon={Calendar}
                title="Sin citas"
                description="La agenda aún no está disponible. No se muestran citas inventadas ni se consulta un API de appointments."
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryStat({ label, value, muted }) {
  return (
    <Card className="border-slate-200">
      <CardContent className="pt-4 pb-4">
        <p className="text-sm text-slate-600">{label}</p>
        <p className={`text-base font-semibold mt-1 leading-snug ${muted ? "text-slate-600" : "text-slate-900"}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function SummaryBlock({ title, items, children }) {
  if (items && !items.length) return null;
  return (
    <div>
      <p className="text-sm font-medium text-slate-900 mb-1">{title}</p>
      {children}
      {items?.length > 0 && (
        <ul className="list-disc pl-4 space-y-0.5">
          {items.map((item, i) => (
            <li key={i} className="text-sm text-slate-800 break-words">
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ScaleGrid({ scales }) {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {scales.map((s) => (
        <li key={s.id} className="border border-slate-200 rounded-lg p-3 min-w-0">
          <p className="text-sm font-medium text-slate-900">{s.label}</p>
          {s.value != null && (
            <p className="text-lg text-slate-900 mt-0.5">{String(s.value)}</p>
          )}
          {s.interpretacion ? (
            <p className="text-sm text-slate-700 mt-1">{s.interpretacion}</p>
          ) : (
            <p className="text-sm text-slate-600 mt-1">Sin interpretación guardada</p>
          )}
        </li>
      ))}
    </ul>
  );
}

function MedicationList({ medications }) {
  if (!medications.items.length) {
    return (
      <DashboardSectionEmpty
        compact
        icon={Pill}
        title="Sin medicamentos"
        description="No hay medicamentos en el paciente ni en la última consulta."
      />
    );
  }
  const sourceLabel =
    medications.source === "paciente"
      ? "Según el expediente del paciente"
      : medications.source === "consulta_actuales"
        ? "Según medicamentos actuales de la última consulta"
        : "Según medicamentos indicados en la última consulta";
  return (
    <div>
      <p className="text-sm text-slate-600 mb-3">{sourceLabel}</p>
      <ul className="space-y-3">
        {medications.items.map((m, i) => (
          <li key={i} className="border-b border-slate-100 last:border-0 pb-2 last:pb-0">
            <p className="text-base font-medium text-slate-900 break-words">{m.nombre}</p>
            {(m.dosis || m.frecuencia) && (
              <p className="text-sm text-slate-700 mt-0.5">
                {m.dosis && <span>Dosis: {m.dosis}</span>}
                {m.dosis && m.frecuencia ? " · " : null}
                {m.frecuencia && <span>Frecuencia: {m.frecuencia}</span>}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
